<script lang="ts">
	import type { TypedQueryDocumentNode } from 'graphql';
	import { onMount } from 'svelte';
	import { getAuthContext } from '../../components/NewsHeader/AuthDialogs/utils/getAuthContext/getAuthContext';
	import { NewsPageBody } from '../../components/NewsPageBody';
	import { GetHeniNewsArticlesDocument } from '../../mutations/__generated__/getHeniNewsArticles.generated';
	import { SearchArticlesByTextDocument } from '../../mutations/__generated__/searchArticlesByText.generated';
	import { NewsContent } from './NewsContent';
	import { getNewsHomepageFilter } from './utils/getNewsHomepageFilter/getNewsHomepageFilter';
	import { getSearchVariables } from './utils/getSearchVariables/getSearchVariables';
	import { infiniteApiQuery } from '#lib/api/infiniteApiQuery/index.js';
	import { FeatureFlags } from '#lib/constants/feature-flags.js';
	import { gqlClientMutations } from '#lib/gqlClientMutations.js';
	import { getNewsArticleSearch } from '#lib/websites/news/api/getNewsArticleSearch.js';
	import { getNewsFeed } from '#lib/websites/news/api/getNewsFeed.js';
	import { NewsProfile } from '#lib/websites/news/pages/NewsHomePage/NewsProfile/index.js';
	import { browser } from '$app/env';
	import { page } from '$app/state';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import { getInfiniteData } from '$global/utils/infinite-loading/getInfiniteData';
	import { infiniteQuery } from '$global/utils/infinite-loading/infiniteQuery';
	import type { Heni_News_Articles } from '$gql/types';
	import { ARTICLES_LIMIT } from '$routes/news/types';

	let isReady = $state(false);

	onMount(() => {
		isReady = true;
	});

	const { hasLoadedCredentials, user, search, createAccountDialogStores } =
		getAuthContext();

	const transformOffset = (offset: number) => {
		return {
			page: `${offset / ARTICLES_LIMIT + 1}`,
		};
	};

	let twoFirstPages = $derived(page.data.twoFirstPages);
	let showGlobalNav = $derived(page.data.showGlobalNav ?? true);
	let profile = $derived(page.data?.profile);
	let searchParams = $derived(page.url.searchParams);
	let filter = $derived(getNewsHomepageFilter(searchParams));

	let phrase = $derived(searchParams.get('phrase'));
	let geotag = $derived(searchParams.get('geotag'));
	let artist = $derived(searchParams.get('artist'));
	let category = $derived(getSearchVariables(searchParams).category ?? null);
	let isHeni = $derived(!!getSearchVariables(searchParams).heni);
	let hasActionTriggered = $state(false);

	let articlesVariables = $derived({
		limit: ARTICLES_LIMIT,
		...(phrase
			? { phrase }
			: {
					...filter,
					offset: 0,
				}),
	});

	type FeedPage = {
		HENI_News_Articles?: Heni_News_Articles[];
		getArticles?: Heni_News_Articles[];
	};

	// Each page keeps the envelope its GraphQL document returns, because the
	// prefetched pages and the transform below both read it. REST article ids
	// are the same uuids; the geotag id is an integer where GraphQL's is a
	// string, which nothing here reads.
	const getRestFeedPage = async (offset: number): Promise<FeedPage> => {
		if (phrase) {
			const data = await getNewsArticleSearch(fetch, {
				phrase,
				limit: ARTICLES_LIMIT,
				page: offset / ARTICLES_LIMIT + 1,
			});
			return data as unknown as FeedPage;
		}

		if (offset === 0 && twoFirstPages?.[0]) {
			return twoFirstPages[0];
		}

		if (offset === ARTICLES_LIMIT && twoFirstPages?.[1]) {
			return twoFirstPages[1];
		}

		const feedFilter: {
			categoryName?: string;
			visibility?: string;
			artistName?: string;
			geotagName?: string;
			isHeni?: boolean;
		} = filter;
		const { isHeni: heniOnly, ...names } = feedFilter;
		const { articles } = await getNewsFeed(fetch, {
			...names,
			...(heniOnly && { isHeni: 'true' as const }),
			limit: ARTICLES_LIMIT,
			offset,
		});
		return { getArticles: articles } as unknown as FeedPage;
	};

	let articlesQuery = $derived(
		FeatureFlags.RestPages['NewsHomePage feed paging and text search']
			? infiniteApiQuery({
					queryKey: ['news-home-feed', 'rest', articlesVariables],
					queryFn: getRestFeedPage,
					limit: ARTICLES_LIMIT,
				})
			: infiniteQuery({
					gqlClient: gqlClientMutations,
					document: phrase
						? (SearchArticlesByTextDocument as TypedQueryDocumentNode<{
								HENI_News_Articles?: Heni_News_Articles[];
								getArticles?: Heni_News_Articles[];
							}>)
						: (GetHeniNewsArticlesDocument as TypedQueryDocumentNode<{
								HENI_News_Articles?: Heni_News_Articles[];
								getArticles?: Heni_News_Articles[];
							}>),
					queryFnOverride: (variables) => {
						if (variables.offset === 0 && twoFirstPages?.[0]) {
							return twoFirstPages[0];
						}

						if (variables.offset === ARTICLES_LIMIT && twoFirstPages?.[1]) {
							return twoFirstPages[1];
						}

						return null;
					},
					transformOffset: phrase ? transformOffset : undefined,
					variables: articlesVariables,
					limit: ARTICLES_LIMIT,
				})
	);

	let loading = $derived(
		(articlesQuery.isFetching && !articlesQuery.isFetchingNextPage) || !isReady
	);
	let loadingNextPage = $derived(articlesQuery.isFetchingNextPage);

	let articles = $derived(
		getInfiniteData({
			query: articlesQuery,
			transform: (data) => {
				const rawArticles =
					(
						data as {
							HENI_News_Articles?: Heni_News_Articles[];
							getArticles?: Heni_News_Articles[];
						}
					)?.HENI_News_Articles ||
					(
						data as {
							HENI_News_Articles?: Heni_News_Articles[];
							getArticles?: Heni_News_Articles[];
						}
					)?.getArticles;

				return (rawArticles || []).map((article) => ({
					...article,
					image:
						article.image && typeof article.image === 'object'
							? getImageUrl(article.image.filename_disk)
							: undefined,
					categories: article.categories
						?.filter((cat) => cat)
						.map((cat) => cat?.HENI_News_Categories_id?.name)
						.filter(Boolean) as string[] | undefined,
					artists: article.artists ?? undefined,
				}));
			},
		})
	);

	$effect(() => {
		if ($hasLoadedCredentials) {
			if (
				!hasActionTriggered &&
				!$user &&
				page.url.searchParams.get('action') === 'signup'
			) {
				createAccountDialogStores.states.open.set(true);
			}

			hasActionTriggered = true;
		}
	});

	// Not `$state`: InfiniteScroll re-fires `onloadmore` synchronously while the
	// scroll position stays within the threshold, and a rune assignment is not
	// visible to a read in that same tick — so a reactive guard lets a second
	// call through. Both calls would then derive the same `getNextPageParam`
	// offset and append the same page twice, duplicating article ids in the
	// flattened feed and breaking the keyed `#each`.
	let loadingMore = false;

	const handleLoadMoreArticles = async () => {
		if (loadingMore) {
			return;
		}

		loadingMore = true;

		try {
			await articlesQuery.fetchNextPage();
		} finally {
			loadingMore = false;
		}
	};

	const onClick = () => {
		search.set('');
	};
</script>

<NewsPageBody
	class="flex min-h-[calc(100vh-96px)] flex-col sm:min-h-[calc(100vh-148px)] md:min-h-[calc(100vh-157px)] lg:min-h-[calc(100vh-152px)]"
>
	{#if profile && browser}
		<NewsProfile />
	{/if}

	<NewsContent
		{articles}
		{loading}
		{loadingNextPage}
		{showGlobalNav}
		{phrase}
		{geotag}
		{artist}
		{category}
		{isHeni}
		hasProfileAbove={Boolean(profile && browser)}
		{onClick}
		{search}
		onLoadMore={handleLoadMoreArticles}
	/>
</NewsPageBody>
